import React, { useEffect, useState } from "react";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  ADD_LANGUAGES_SKILLS_LABEL,
  CANCEL_LABEL,
  CONFIRM_LABEL,
  EDIT_LANGUAGES_SKILLS_LABEL,
  IS_REQUIRED_LABEL,
  LANGUAGE_LABEL,
  NATIVE_LANGUAGE_LABEL,
} from "../../../../../../../constants/Dictionary";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../../../components/core/Select/Select";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import {
  StyledLanguageSkillsSection,
  StyledLanguageSkillsContainer,
  StyledLanguageSkillsFooter,
} from "./LanguageSkillsModal.styled";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../icons/close.svg";
import { ReactComponent as LighbulbIcon } from "../../../../../../../icons/lightbulb.svg";
import { ReactComponent as AnnouncementIcon } from "../../../../../../../icons/announcement.svg";
import { ReactComponent as Written } from "../../../../../../../icons/written.svg";
import ToggleSwitch from "../../../../../../../components/core/ToggleSwitch/ToggleSwitch";
import { BodyTyp } from "../../../../../../../components/Typography/Typography";
import { FontWeightEnum } from "../../../../../../../types/AppTypes";
import { Divider } from "../../../../../../../components/core/Divider/Divider";
import { LanguageSlider } from "../../../../../../../components/LanguageSlider/LanguageSlider";
import { FormSection } from "../../../../../../../components/FormSection/FormSection";
import { useAppSelector } from "../../../../../../../hooks/store.hook";
import {
  CEFR_LEVELS,
  LanguageLevelEnum,
  maxLevel,
  messageFooter,
  minLevel,
  SKILL_KEYS,
} from "../../../../../../../constants/LanguageConstants";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  AddCollaboratorsHistorySkillLanguages,
  CollaboratorDetails,
  CollaboratorsHistorySkillLanguages,
  UpdateCollaboratorsHistorySkillLanguages,
} from "../../../../../../../types/api/collaborators.type";
import { LanguageGroup } from "../../../../../../../constants/AppConstants";
import { findLabelFromValue } from "../../../../../../../utils/orgFunctions.utils";
import {
  useHistoryLanguageAdd,
  useHistoryLanguageUpdate,
} from "../../../../../../../hooks/professionalHistory.hook";

type FormData = {
  language: string | null;
  nativeLanguage: boolean;
  listening: number;
  reading: number;
  spokenInteraction: number;
  spokenProduction: number;
  written: number;
};

type LanguageSkillsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  record?: CollaboratorsHistorySkillLanguages | null;
  userLanguage?: string[];
  collaborator: CollaboratorDetails;
};

const SKILL_GROUPS: LanguageGroup[] = [
  {
    groupLabel: "COMPRENSIONE",
    icon: <LighbulbIcon />,
    skills: [
      { key: SKILL_KEYS.LISTENING, title: "Ascolto" },
      { key: SKILL_KEYS.READING, title: "Lettura" },
    ],
  },
  {
    groupLabel: "PARLATO",
    icon: <AnnouncementIcon />,
    skills: [
      { key: SKILL_KEYS.SPOKEN_INTERACTION, title: "Interazione" },
      { key: SKILL_KEYS.SPOKEN_PRODUCTION, title: "Produzione Orale" },
    ],
  },
  {
    groupLabel: "SCRITTO",
    icon: <Written />,
    skills: [{ key: SKILL_KEYS.WRITTEN, title: "" }],
  },
];

export const LanguageSkillsModal = ({
  isOpen,
  onClose,
  onSave,
  record,
  userLanguage,
  collaborator,
}: LanguageSkillsModalProps) => {
  const { orgSettings } = useAppSelector((state) => state.config);
  const [searchValue, setSearchValue] = useState("");

  const profileId = collaborator.id;

  const { mutateAsync: addLanguage, isPending: pendingAddLanguage } =
    useHistoryLanguageAdd(profileId);
  const { mutateAsync: updateLanguage, isPending: pendingUpdateLanguage } =
    useHistoryLanguageUpdate(profileId);

  const levelFieldSchema = yup
    .number()
    .min(0, "Il livello minimo è A1")
    .max(5, "Il livello massimo è C2")
    .required();

  const schema = yup.object({
    language: yup
      .string()
      .required(`${LANGUAGE_LABEL} ${IS_REQUIRED_LABEL}`)
      .nullable(),
    nativeLanguage: yup.boolean().required(),
    listening: levelFieldSchema,
    reading: levelFieldSchema,
    spokenInteraction: levelFieldSchema,
    spokenProduction: levelFieldSchema,
    written: levelFieldSchema,
  });

  const defaultValues = (): FormData => ({
    language: null,
    nativeLanguage: false,
    listening: 0,
    reading: 0,
    spokenInteraction: 0,
    spokenProduction: 0,
    written: 0,
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const buildAddLanguageSkillPayload = (
    formData: FormData,
  ): AddCollaboratorsHistorySkillLanguages => {
    return {
      language: formData.language!,
      nativeLanguage: formData.nativeLanguage,
      listeningComprehension: CEFR_LEVELS[formData.listening],
      readingComprehension: CEFR_LEVELS[formData.reading],
      spokenInteraction: CEFR_LEVELS[formData.spokenInteraction],
      spokenOralProduction: CEFR_LEVELS[formData.spokenProduction],
      written: CEFR_LEVELS[formData.written],
    };
  };

  const buildUpdateLanguageSkillPayload = (
    formData: FormData,
  ): UpdateCollaboratorsHistorySkillLanguages => ({
    nativeLanguage: formData.nativeLanguage,
    listeningComprehension: CEFR_LEVELS[formData.listening],
    readingComprehension: CEFR_LEVELS[formData.reading],
    spokenInteraction: CEFR_LEVELS[formData.spokenInteraction],
    spokenOralProduction: CEFR_LEVELS[formData.spokenProduction],
    written: CEFR_LEVELS[formData.written],
  });

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (!record) {
      const addBody = buildAddLanguageSkillPayload(data);
      await addLanguage(addBody);
    } else {
      const updateBody = buildUpdateLanguageSkillPayload(data);
      await updateLanguage({ record, updateBody });
    }
    closeModal();
    onSave();
  };

  const closeModal = () => {
    reset(defaultValues());
    setSearchValue("");
    onClose();
  };

  const handleToggleChange = (checked: boolean) => {
    const level = checked ? maxLevel : minLevel;

    setValue("listening", level, { shouldDirty: true });
    setValue("reading", level, { shouldDirty: true });
    setValue("spokenInteraction", level, { shouldDirty: true });
    setValue("spokenProduction", level, { shouldDirty: true });
    setValue("written", level, { shouldDirty: true });
  };

  const filteredLanguageOptions =
    orgSettings.languageSkills.languages?.filter((opt) => {
      const matchesSearch = opt.label
        .toLowerCase()
        .includes(searchValue.toLowerCase());
      const isAvailableLanguage =
        !userLanguage?.includes(opt.value) &&
        (!record || opt.value !== record?.language);
      return matchesSearch && isAvailableLanguage;
    }) ?? [];

  const convertCEFRToNumber = (level?: LanguageLevelEnum): number => {
    return CEFR_LEVELS.indexOf(level ?? LanguageLevelEnum.A1);
  };

  useEffect(() => {
    if (record) {
      reset({
        language: record.language,
        nativeLanguage: record.nativeLanguage,
        [SKILL_KEYS.LISTENING]: convertCEFRToNumber(
          record.listeningComprehension,
        ),
        [SKILL_KEYS.READING]: convertCEFRToNumber(record.readingComprehension),
        [SKILL_KEYS.SPOKEN_INTERACTION]: convertCEFRToNumber(
          record.spokenInteraction,
        ),
        [SKILL_KEYS.SPOKEN_PRODUCTION]: convertCEFRToNumber(
          record.spokenOralProduction,
        ),
        [SKILL_KEYS.WRITTEN]: convertCEFRToNumber(record.written),
      });
    } else {
      reset(defaultValues());
    }
  }, [record, isOpen]);

  const enableSliders = watch("nativeLanguage");

  return (
    <Modal
      fullSize={false}
      label={record ? EDIT_LANGUAGES_SKILLS_LABEL : ADD_LANGUAGES_SKILLS_LABEL}
      isOpen={isOpen}
      leftButton={
        <Button
          label={CANCEL_LABEL}
          variant={ButtonVariants.SECONDARY}
          icon={<CloseIcon />}
          onClick={closeModal}
          disabled={pendingAddLanguage || pendingUpdateLanguage}
        />
      }
      rightButton={
        <Button
          label={CONFIRM_LABEL}
          variant={ButtonVariants.PRIMARY}
          icon={<CheckIcon />}
          disabled={!isDirty || pendingAddLanguage || pendingUpdateLanguage}
          type="submit"
          form="languageSkillsForm"
        />
      }
      message={
        <BodyTyp
          key="footer-message"
          variant="2"
          fontWeight={FontWeightEnum.REGULAR}
        >
          {messageFooter.map(({ level, label }) => (
            <StyledLanguageSkillsFooter key={label}>
              <strong>{level}</strong> {label}
            </StyledLanguageSkillsFooter>
          ))}
        </BodyTyp>
      }
    >
      <StyledLanguageSkillsSection>
        <Loader size={30} loading={pendingAddLanguage || pendingUpdateLanguage}>
          <form id="languageSkillsForm" onSubmit={handleSubmit(onSubmit)}>
            <div className="input-row">
              {!record ? (
                <FormField error={errors.language?.message}>
                  <Controller
                    name="language"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.DEFAULT}
                        label={LANGUAGE_LABEL}
                        options={filteredLanguageOptions}
                        {...field}
                        searchValue={searchValue}
                        onSearch={setSearchValue}
                        withSearch
                        id="select-language"
                      />
                    )}
                  />
                </FormField>
              ) : (
                <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                  {findLabelFromValue(
                    orgSettings.languageSkills.languages,
                    record.language,
                  )}
                </BodyTyp>
              )}

              <FormField error={errors.nativeLanguage?.message}>
                <div className="toggle-row">
                  <Controller
                    name={"nativeLanguage"}
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <ToggleSwitch
                        checked={value ?? false}
                        onChange={(checked) => {
                          onChange(checked);
                          handleToggleChange(checked);
                        }}
                      />
                    )}
                  />
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                    {NATIVE_LANGUAGE_LABEL}
                  </BodyTyp>
                </div>
              </FormField>
            </div>
            {SKILL_GROUPS.map((group) => (
              <React.Fragment key={group.groupLabel}>
                <Divider />
                <FormSection
                  key={group.groupLabel}
                  label={group.groupLabel}
                  icon={group.icon}
                >
                  <StyledLanguageSkillsContainer>
                    {group.skills.map((skill) => {
                      return (
                        <Controller
                          key={skill.key}
                          name={skill.key as keyof FormData}
                          control={control}
                          defaultValue={minLevel}
                          render={({ field }) => (
                            <LanguageSlider
                              title={skill.title}
                              onChange={(newValue) => {
                                if (!enableSliders) {
                                  field.onChange(newValue);
                                }
                              }}
                              value={
                                typeof field.value === "number"
                                  ? field.value
                                  : minLevel
                              }
                              disabled={enableSliders}
                            />
                          )}
                        />
                      );
                    })}
                  </StyledLanguageSkillsContainer>
                </FormSection>
              </React.Fragment>
            ))}
          </form>
        </Loader>
      </StyledLanguageSkillsSection>
    </Modal>
  );
};
